import {
	type EdgeConfigurationDraft,
	edgeConfigurationDraftSchema,
} from "@sitescene/shared";

import type { Integration, Source } from "./editor";

export type ActiveForm =
	| { baseline?: Integration; item: Integration; kind: "integrations" }
	| { baseline?: Source; item: Source; kind: "sources" };
export type FieldErrors = Record<string, string>;
export function formDirty(form: ActiveForm | undefined) {
	return (
		!!form &&
		(!form.baseline || semanticKey(form.item) !== semanticKey(form.baseline))
	);
}
export function openForm(
	draft: EdgeConfigurationDraft,
	kind: ActiveForm["kind"],
	id: string,
): ActiveForm | undefined {
	if (kind === "sources") {
		const item = draft.sources.find((item) => item.id === id);
		return item
			? { baseline: item, item: structuredClone(item), kind }
			: undefined;
	}
	const item = draft.integrations.find((item) => item.id === id);
	return item
		? { baseline: item, item: structuredClone(item), kind }
		: undefined;
}
export function semanticKey(value: unknown): string {
	const canonical = (item: unknown): unknown =>
		Array.isArray(item)
			? item.map(canonical)
			: item && typeof item === "object"
				? Object.fromEntries(
						Object.entries(item)
							.filter(([, v]) => v !== undefined && v !== null && v !== "")
							.sort(([a], [b]) => a.localeCompare(b))
							.map(([k, v]) => [k, canonical(v)]),
					)
				: item;
	return JSON.stringify(canonical(value));
}
export function stageForm(
	draft: EdgeConfigurationDraft,
	form: ActiveForm,
): EdgeConfigurationDraft {
	if (form.kind === "sources")
		return {
			...draft,
			sources: draft.sources.some((item) => item.id === form.item.id)
				? draft.sources.map((item) =>
						item.id === form.item.id ? form.item : item,
					)
				: [...draft.sources, form.item],
		};
	return {
		...draft,
		integrations: draft.integrations.some((item) => item.id === form.item.id)
			? draft.integrations.map((item) =>
					item.id === form.item.id ? form.item : item,
				)
			: [...draft.integrations, form.item],
	};
}
const fieldMessages: Record<string, string> = {
	host: "Enter a hostname or IP address, without a URL scheme or path.",
	name: "Enter a name (up to 120 characters, on one line).",
	onb_host: "Enter the video bridge hostname or IP address.",
	onb_port: "Enter a whole-number port from 1 to 65535.",
	port: "Enter a whole-number port from 1 to 65535.",
	segment_duration: "Enter whole seconds from 1 to 300.",
	sensor_host: "Enter a hostname or IP address, without a URL scheme or path.",
	sensor_id:
		"Use 1–128 letters, numbers, dots, underscores or hyphens; begin with a letter, number, underscore or hyphen.",
	sensor_port: "Enter a whole-number port from 1 to 65535.",
	video_bitrate: "Enter a whole number from 100 to 50000 kbps, or leave empty.",
	video_fps: "Enter a whole number from 1 to 60, or leave empty.",
	video_resolution: "Use widthxheight, for example 1920x1080, or leave empty.",
	video_sensor_id: "Enter the associated video sensor ID.",
	zone_id: "Use a zone ID with letters, numbers, dots, underscores or hyphens.",
};
export function draftErrors(
	draft: EdgeConfigurationDraft,
	unreadableSourceUrls: string[] = [],
): Record<string, FieldErrors> {
	const errors: Record<string, FieldErrors> = {};
	// Keeping a URL the key can't decrypt would publish a source the device can't
	// download, so it must be replaced or the source removed.
	for (const source of draft.sources)
		if (
			unreadableSourceUrls.includes(source.id) &&
			source.secrets.rtsp_url?.operation === "keep"
		)
			(errors[source.id] ??= {}).rtsp_url =
				"Enter the stream URL again to replace the unreadable one.";
	const result = edgeConfigurationDraftSchema.safeParse(draft);
	if (result.success) return errors;
	for (const issue of result.error.issues) {
		const [kind, index, section, key] = issue.path;
		const entity =
			(kind === "sources" || kind === "integrations") &&
			typeof index === "number"
				? draft[kind][index]
				: undefined;
		let field = typeof key === "string" ? key : "form";
		let message =
			fieldMessages[field] ??
			(section === "secrets"
				? "Enter a value or choose a stored credential."
				: issue.code === "custom"
					? issue.message
					: "Check this value.");
		if (issue.code === "custom") {
			message = issue.message;
			const map: [string, string, string?][] = [
				[
					"Video delivery requires",
					"zone_id",
					"Enter a Zone ID and select MP4 for video delivery.",
				],
				["RTSP sources require", "rtsp_url", "Enter an RTSP stream URL."],
				[
					"Invalid RTSP URL",
					"rtsp_url",
					"Enter a URL starting with rtsp:// or rtsps://, without spaces.",
				],
				[
					"Sensor identifiers must",
					"sensor_id",
					"This Sensor ID is already used by another source.",
				],
				[
					"Home Assistant requires an entity",
					"ha_entity_id",
					"Enter a Home Assistant entity ID.",
				],
				[
					"Home Assistant requires a token",
					"auth_token",
					"Enter the Home Assistant access token.",
				],
				["Integration is missing", "integration_id"],
				["Complete the source host", "sensor_host"],
				["Milestone requires port", "password"],
				["Complete the video bridge", "onb_host"],
				["Scene events require", "video_sensor_id"],
				["CA bundle", "ca_bundle_pem"],
			];
			const mapping = map.find(([prefix]) => message.startsWith(prefix));
			if (mapping) {
				field = mapping[1];
				message = mapping[2] ?? message;
			}
		}
		if (!entity)
			message =
				issue.code === "too_big"
					? "The draft supports up to 200 sources and 100 integrations."
					: "Check the configuration format.";
		const group = entity?.id ?? "draft";
		(errors[group] ??= {})[field] ??= message;
	}
	return errors;
}
